import React, { useEffect, useMemo, useState } from 'react';
import { Image as ImageIcon, Video, Calendar, Play, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { apiService } from '../services/api';

const ITEMS_PER_PAGE = 9;

const getTimestamp = (item) => {
  const rawDate = item?.date || item?.created_at || item?.updated_at;
  const parsed = rawDate ? new Date(rawDate).getTime() : 0;
  return Number.isNaN(parsed) ? 0 : parsed;
};

const matchesSearch = (item, query) => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return true;
  }

  return [item?.title, item?.event, item?.description]
    .filter(Boolean)
    .some((value) => value.toLowerCase().includes(normalizedQuery));
};

const Galeri = () => {
  const [activeTab, setActiveTab] = useState('photos');
  const [eventFilter, setEventFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadGallery = async () => {
      try {
        setLoading(true);
        setError('');
        const [photosRes, videosRes] = await Promise.all([
          apiService.getPhotos(),
          apiService.getVideos(),
        ]);
        setPhotos(Array.isArray(photosRes.data) ? photosRes.data : []);
        setVideos(Array.isArray(videosRes.data) ? videosRes.data : []);
      } catch (err) {
        console.error('Error loading gallery:', err);
        setError('Gagal memuat galeri.');
      } finally {
        setLoading(false);
      }
    };

    loadGallery();
  }, []);

  const photoEvents = useMemo(() => ['all', ...new Set(photos.map((p) => p.event).filter(Boolean))], [photos]);
  const videoEvents = useMemo(() => ['all', ...new Set(videos.map((v) => v.event).filter(Boolean))], [videos]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, eventFilter, searchQuery, sortOrder]);

  const filteredPhotos = useMemo(() => {
    const filtered = photos.filter((photo) => {
      const matchesEvent = eventFilter === 'all' || photo.event === eventFilter;
      return matchesEvent && matchesSearch(photo, searchQuery);
    });

    return [...filtered].sort((left, right) => {
      const diff = getTimestamp(right) - getTimestamp(left);
      return sortOrder === 'newest' ? diff : -diff;
    });
  }, [eventFilter, photos, searchQuery, sortOrder]);

  const filteredVideos = useMemo(() => {
    const filtered = videos.filter((video) => {
      const matchesEvent = eventFilter === 'all' || video.event === eventFilter;
      return matchesEvent && matchesSearch(video, searchQuery);
    });

    return [...filtered].sort((left, right) => {
      const diff = getTimestamp(right) - getTimestamp(left);
      return sortOrder === 'newest' ? diff : -diff;
    });
  }, [eventFilter, searchQuery, sortOrder, videos]);

  const activeItems = activeTab === 'photos' ? filteredPhotos : filteredVideos;
  const totalPages = Math.max(1, Math.ceil(activeItems.length / ITEMS_PER_PAGE));
  const paginatedItems = activeItems.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <p className="text-gray-500 text-lg">Memuat galeri...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <p className="text-red-600 text-lg">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Galeri</h1>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-8">
          <div className="flex border-b">
            <button
              onClick={() => {
                setActiveTab('photos');
                setEventFilter('all');
              }}
              className={`flex items-center space-x-2 px-6 py-4 font-semibold transition-colors ${
                activeTab === 'photos'
                  ? 'border-b-2 border-green-600 text-green-600'
                  : 'text-gray-600 hover:text-green-600'
              }`}
            >
              <ImageIcon className="h-5 w-5" />
              <span>Foto</span>
            </button>
            <button
              onClick={() => {
                setActiveTab('videos');
                setEventFilter('all');
              }}
              className={`flex items-center space-x-2 px-6 py-4 font-semibold transition-colors ${
                activeTab === 'videos'
                  ? 'border-b-2 border-green-600 text-green-600'
                  : 'text-gray-600 hover:text-green-600'
              }`}
            >
              <Video className="h-5 w-5" />
              <span>Video</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(220px,0.8fr)_minmax(220px,0.8fr)]">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cari
              </label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={`Cari ${activeTab === 'photos' ? 'foto' : 'video'}, event, atau judul`}
                  className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-4 focus:border-transparent focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter Event
              </label>
              <select
                value={eventFilter}
                onChange={(e) => setEventFilter(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-green-500"
              >
                {(activeTab === 'photos' ? photoEvents : videoEvents).map((event) => (
                  <option key={event} value={event}>
                    {event === 'all' ? 'Semua Event' : event}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Urutkan
              </label>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-green-500"
              >
                <option value="newest">Terbaru ke terlama</option>
                <option value="oldest">Terlama ke terbaru</option>
              </select>
            </div>
          </div>
        </div>

        {/* Photos Grid */}
        {activeTab === 'photos' && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {paginatedItems.map((photo) => (
              <div
                key={photo.id}
                className="group relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-200 aspect-square bg-gray-200"
              >
                <img
                  src={photo.image}
                  alt={photo.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-200"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-white font-semibold text-sm line-clamp-2 mb-2">{photo.title}</h3>
                    <div className="flex items-center text-white/80 text-xs space-x-2">
                      <Calendar className="h-3 w-3" />
                      <span>{new Date(photo.date).toLocaleDateString('id-ID')}</span>
                    </div>
                    <p className="text-white/60 text-xs mt-1">{photo.event}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Videos Grid */}
        {activeTab === 'videos' && (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {paginatedItems.map((video) => (
              <div
                key={video.id}
                className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-200"
              >
                {(() => {
                  const youtubeId = video.youtubeId || video.youtube_id;
                  if (!youtubeId) {
                    return (
                      <div className="p-4 text-sm text-gray-500">
                        Video tidak memiliki YouTube ID yang valid.
                      </div>
                    );
                  }

                  return (
                    <>
                      <div className="relative group cursor-pointer">
                        <a
                          href={`https://www.youtube.com/watch?v=${youtubeId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img
                            src={`https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`}
                            alt={video.title}
                            className="w-full h-48 object-cover"
                            onError={(e) => {
                              e.currentTarget.src = `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;
                            }}
                          />
                          <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors flex items-center justify-center">
                            <div className="bg-red-600 rounded-full p-4 group-hover:scale-110 transition-transform">
                              <Play className="h-8 w-8 text-white fill-white" />
                            </div>
                          </div>
                        </a>
                      </div>
                      <div className="p-4">
                        <h3 className="font-semibold text-gray-900 line-clamp-2 mb-2">{video.title}</h3>
                        <div className="flex items-center justify-between text-sm text-gray-500">
                          <span>{video.event}</span>
                          <div className="flex items-center space-x-1">
                            <Calendar className="h-3 w-3" />
                            <span>{new Date(video.date).toLocaleDateString('id-ID')}</span>
                          </div>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            ))}
          </div>
        )}

        {((activeTab === 'photos' && filteredPhotos.length === 0) || 
          (activeTab === 'videos' && filteredVideos.length === 0)) && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">
              Tidak ada {activeTab === 'photos' ? 'foto' : 'video'} ditemukan.
            </p>
          </div>
        )}

        {activeItems.length > 0 && totalPages > 1 && (
          <div className="mt-8 flex flex-col items-center justify-between gap-4 rounded-lg bg-white px-4 py-4 shadow-md sm:flex-row">
            <p className="text-sm text-gray-600">
              Menampilkan {(currentPage - 1) * ITEMS_PER_PAGE + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, activeItems.length)} dari {activeItems.length} {activeTab === 'photos' ? 'foto' : 'video'}
            </p>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                disabled={currentPage === 1}
                className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition hover:border-green-500 hover:text-green-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4" />
                Sebelumnya
              </button>

              <span className="min-w-[110px] text-center text-sm font-medium text-gray-700">
                Halaman {currentPage} / {totalPages}
              </span>

              <div className="hidden md:flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, idx) => idx + 1).slice(
                  Math.max(0, currentPage - 3),
                  Math.min(totalPages, currentPage + 2)
                ).map((pageNumber) => (
                  <button
                    key={pageNumber}
                    type="button"
                    onClick={() => setCurrentPage(pageNumber)}
                    className={`h-8 min-w-[2rem] rounded-md border px-2 text-sm font-medium transition ${
                      currentPage === pageNumber
                        ? 'border-green-600 bg-green-600 text-white'
                        : 'border-gray-300 text-gray-700 hover:border-green-500 hover:text-green-600'
                    }`}
                  >
                    {pageNumber}
                  </button>
                ))}
              </div>

              <button
                type="button"
                onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                disabled={currentPage === totalPages}
                className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition hover:border-green-500 hover:text-green-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Berikutnya
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Galeri;